import {createSelector} from '@reduxjs/toolkit'

import {Booking} from '@vio/core/src/api-types/bovio/response/booking'
import {getUser} from '@vio/core/src/auth/modules/selectors'
import {validateGuestName} from '@vio/core/src/utils/validation/nameValidationRules'

import {RootState} from '../../type'
import {DealFreezeAnalyticsContext, SearchDetail, State} from './slice'

export const getIsOnboardingOverlayOpen = (state: RootState): boolean =>
  state.dealFreeze.isOnboardingOverlayOpen

export const getOnboardingContext = (state: RootState) =>
  state.dealFreeze.onboardingContext

export const getFreezeOverlayOpenedFromOnboarding = (
  state: RootState
): boolean => state.dealFreeze.freezeOverlayOpenedFromOnboarding

export const getIsFreezeOverlayOpen = createSelector(
  [(state: RootState) => state.dealFreeze.isOverlayOpen],
  isOverlayOpen => isOverlayOpen
)

export const getIsResumePending = (state: RootState): boolean =>
  state.dealFreeze.isResumePending

export const getShouldAutoFreeze = (state: RootState): boolean =>
  state.dealFreeze.shouldAutoFreeze

export const getResumeOutcome = (state: RootState) =>
  state.dealFreeze.resumeOutcome

// Rooms poll for THIS hotel terminally errored (cache never flips complete on error).
export const getResumeRoomsErrored = (
  state: RootState,
  hotelId?: string
): boolean =>
  Boolean(hotelId) && state.dealFreeze.resumeRoomsErrorHotelId === hotelId

export const getIsFreezeConfirmationOverlayOpen = (state: RootState) =>
  state.dealFreeze.isConfirmationOverlayOpen

export const getIsUserEligibleForDealFreeze = (state: RootState) =>
  state.dealFreeze.isUserEligible

export const getHasCheckedUserLimit = (state: RootState) =>
  state.dealFreeze.hasCheckedUserLimit

export const getIsGuestDetailsStepOpen = (state: RootState): boolean =>
  state.dealFreeze.guestDetails.isStepOpen

export const getGuestDetailsStepOpenedFrom = (state: RootState) =>
  state.dealFreeze.guestDetails.stepOpenedFrom

export const getGuestName = createSelector(
  [(state: RootState) => state.dealFreeze.guestDetails, getUser],
  ({firstName, lastName}, user) => ({
    firstName: firstName ?? user?.attributes?.given_name ?? '',
    lastName: lastName ?? user?.attributes?.family_name ?? ''
  })
)

export const getGuestNameValidation = createSelector(
  [getGuestName],
  ({firstName, lastName}) => validateGuestName(firstName, lastName)
)

export const getFreezeSelectedContext = createSelector(
  [(state: RootState) => state.dealFreeze.data],
  ({selectedOffer, selectedHotel, searchDetail, rooms, sapiMatchedOffer}) => {
    return {
      offer: selectedOffer,
      hotel: selectedHotel,
      searchDetail: searchDetail as SearchDetail,
      rooms,
      sapiMatchedOffer
    }
  }
)

export const getOfferEligibility = (state: RootState) => {
  return (state.dealFreeze.offerCheck || {}) as State['offerCheck']
}

export const getIsDealFreezeEnabled = (state: RootState): boolean => {
  return state.dealFreeze.isDealFreezeEnabled || false
}

export const getBookingStatus = (state: RootState) => {
  return (state.dealFreeze.bookingStatus || {}) as State['bookingStatus']
}

const EMPTY_ANALYTICS_CONTEXT: DealFreezeAnalyticsContext = {}

export const getAnalyticsContext = (state: RootState) => {
  return (state.dealFreeze.analyticsContext ??
    EMPTY_ANALYTICS_CONTEXT) as DealFreezeAnalyticsContext
}

export const getAnalyticsLeadContext = createSelector(
  [(state: RootState) => state.dealFreeze.analyticsContext?.leadContext],
  leadContext => leadContext
)

export const getAnalyticsOfferContext = createSelector(
  [(state: RootState) => state.dealFreeze.analyticsContext?.offerContext],
  offerContext => offerContext
)

export const getAnalyticsPriceContext = createSelector(
  [(state: RootState) => state.dealFreeze.analyticsContext?.priceContext],
  priceContext => priceContext
)

export const getApiContext = (state: RootState) => {
  return state.dealFreeze.api
}

export const getError = createSelector(
  [(state: RootState) => state.dealFreeze.error],
  error => error
)

export const getFirstEligibleDealFreezeRoom = (state: RootState) => {
  return state.dealFreeze.firstEligibleDealFreezeRoomId
}

/**
 * Since Deal Freeze is located outside the `client` and `checkout` packages,
 * it doesn't have direct access to their respective values.
 * `application` on Redux provides essential metadata like the version of the `client` or `checkout`,
 * searchId, page, countryCode, and language when Deal Freeze is in use.
 * Centralizing this information aids in debugging and ensures efficient access to these values when required.
 *
 * @param state - RootState
 */
export const getApplication = createSelector(
  [(state: RootState) => state.dealFreeze.application],
  application => {
    return {
      ...application
    }
  }
)
export const getComponent = (state: RootState) =>
  state.dealFreeze.data.component

export const getIsOfferViewTracked = (state: RootState, offerId: string) => {
  const {trackedOffers} = state.dealFreeze.analytics
  const trackId = `${offerId}`
  return trackedOffers.includes(trackId)
}

export const getFrozenBookingByHotelId = (
  state: RootState,
  hotelId?: string
) => {
  const {frozenBookings} = state.dealFreeze
  if (!frozenBookings || !hotelId) return undefined
  return frozenBookings.find(booking => booking.hotelId === hotelId) as
    | Booking
    | undefined
}

// `frozenBookings` is populated from `bookings?status=frozen`, so the list is
// already filtered to active freezes by the backend. No client-side status
// filter needed (sibling selectors in this file follow the same convention).
export const getActiveFrozenBookingCount = (state: RootState): number =>
  state.dealFreeze.frozenBookings?.length ?? 0

export const getFrozenBookingsByRoomIds = (
  state: RootState,
  roomIds?: string[]
) => {
  const {frozenBookings} = state.dealFreeze

  if (!frozenBookings || !roomIds?.length) return []

  return frozenBookings.filter(booking =>
    booking.rooms.some(room => room.id && roomIds.includes(room.id))
  ) as Booking[]
}

/**
 * Given an already-filtered list of bookings, returns the one matching the
 * current search dates. Falls back to the first booking when dates are not yet
 * available (e.g. Redux state not populated during page load).
 */
export const pickFrozenBookingByItinerary = (
  bookings: Booking[],
  checkIn: string | undefined,
  checkOut: string | undefined
): Booking | undefined => {
  if (!bookings.length) return undefined
  if (checkIn && checkOut)
    return bookings.find(b => b.checkIn === checkIn && b.checkOut === checkOut)
  return bookings[0]
}

export const getFrozenBookingByItinerary = (
  state: RootState,
  checkIn: string,
  checkOut: string,
  hotelId?: string
) => {
  const {frozenBookings} = state.dealFreeze
  if (!frozenBookings || !hotelId || !checkIn || !checkOut) return undefined
  return frozenBookings.find(
    booking =>
      booking.hotelId === hotelId &&
      booking.checkIn === checkIn &&
      booking.checkOut === checkOut
  ) as Booking | undefined
}

export const getOfferEncounteredError = (state: RootState, offerId?: string) =>
  offerId ? state.dealFreeze.offersWithError?.includes(offerId) : false
